import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { TrustedAdvisorDashboard as TrustedAdvisorDashboardType } from '../../types/trustedAdvisor';
import TrustedAdvisorDashboard from './TrustedAdvisorDashboard';
import TrustedAdvisorList from './TrustedAdvisorList';
import PermissionMatrix from './PermissionMatrix';
import UnlockRequests from './UnlockRequests';
import EmergencyContacts from './EmergencyContacts';
import AuditLogs from './AuditLogs';
import TrustedAdvisorInvitationModal from './TrustedAdvisorInvitationModal';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Badge } from '../common/Badge';
import { 
  Users, 
  Shield, 
  FileText, 
  Unlock, 
  Phone, 
  Activity,
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  Bell
} from 'lucide-react';

type TabType = 'dashboard' | 'advisors' | 'permissions' | 'unlock-requests' | 'emergency-contacts' | 'audit-logs';

const TrustedAdvisorManagement: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [dashboard, setDashboard] = useState<TrustedAdvisorDashboardType | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(3);

  useEffect(() => {
    if (!user) {
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    // Load dashboard when component mounts or user changes
    if (!dashboard) {
      loadDashboard();
    }
  }, [user]);

  const loadDashboard = async () => {
    // Prevent multiple simultaneous loads
    if (loading) {
      console.log('🔄 Dashboard load already in progress, skipping...');
      return;
    }

    try {
      console.log('🔄 Loading trusted advisor dashboard...');
      setLoading(true);
      setError(null);
      setRetryCount(0); // Reset retry count on new load
      
      const dashboardData = await TrustedAdvisorService.getDashboard();
      setDashboard(dashboardData);
      setLastRefresh(new Date());
      console.log('✅ Dashboard loaded successfully');
    } catch (err) {
      console.error('❌ Failed to load dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInvitationSuccess = () => {
    setShowInvitationModal(false);
    loadDashboard(); // Refresh data
  };

  const getTabBadge = (tabId: TabType) => {
    if (!dashboard) return null;
    
    switch (tabId) {
      case 'advisors':
        return dashboard.summary.pending_advisors > 0 ? dashboard.summary.pending_advisors : null;
      case 'unlock-requests':
        return dashboard.pending_unlock_requests.length > 0 ? dashboard.pending_unlock_requests.length : null;
      case 'audit-logs':
        return dashboard.unread_notifications > 0 ? dashboard.unread_notifications : null;
      case 'pending-invitations':
        return dashboard.pending_invitations_count > 0 ? dashboard.pending_invitations_count : null;
      default:
        return null;
    }
  };

  const getTabAlert = (tabId: TabType) => {
    if (!dashboard) return false;
    
    switch (tabId) {
      case 'advisors':
        return dashboard.summary.pending_advisors > 0;
      case 'unlock-requests':
        return dashboard.pending_unlock_requests.length > 0;
      case 'audit-logs':
        return dashboard.unread_notifications > 0;
      case 'pending-invitations':
        return dashboard.pending_invitations_count > 0;
      default:
        return false;
    }
  };

  const tabs = [
    {
      id: 'dashboard' as TabType,
      label: 'Dashboard',
      icon: <Shield className="w-4 h-4" />,
      description: 'Overview and key metrics'
    },
    {
      id: 'advisors' as TabType,
      label: 'Trusted Advisors',
      icon: <Users className="w-4 h-4" />,
      description: 'Manage advisor relationships'
    },
    {
      id: 'permissions' as TabType,
      label: 'Permissions',
      icon: <FileText className="w-4 h-4" />,
      description: 'Configure document access'
    },
    {
      id: 'unlock-requests' as TabType,
      label: 'Unlock Requests',
      icon: <Unlock className="w-4 h-4" />,
      description: 'Post-death access requests'
    },
    {
      id: 'emergency-contacts' as TabType,
      label: 'Emergency Contacts',
      icon: <Phone className="w-4 h-4" />,
      description: 'Emergency access management'
    },
    {
      id: 'audit-logs' as TabType,
      label: 'Activity Log',
      icon: <Activity className="w-4 h-4" />,
      description: 'System activity tracking'
    },

  ];

  if (error && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-6 max-w-md">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <Shield className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Error Loading Trusted Advisors</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="space-y-2">
              <Button 
                onClick={() => {
                  setRetryCount(0);
                  setError(null);
                  loadDashboard();
                }} 
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Retrying...' : 'Try Again'}
              </Button>
              {retryCount > 0 && (
                <p className="text-sm text-gray-500">
                  Retry attempt: {retryCount}/{maxRetries}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-idecide-bg">
      {/* Header */}
      <div className="bg-white border-b border-idecide-primary/30">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between h-auto sm:h-16 py-4 sm:py-0 gap-4 sm:gap-0">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-idecide-primary mr-3" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-idecide-dark">
                  Trusted Advisor Management
                </h1>
                <p className="text-sm text-idecide-secondary">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              {dashboard?.unread_notifications > 0 && (
                <Badge variant="warning" className="flex items-center bg-idecide-warning text-idecide-dark">
                  <Bell className="w-4 h-4 mr-1" />
                  {dashboard.unread_notifications} notifications
                </Badge>
              )}
              <Button
                onClick={loadDashboard}
                variant="outline"
                className="flex items-center border-idecide-primary text-idecide-primary hover:bg-idecide-primary/10 text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                onClick={() => setShowInvitationModal(true)}
                variant="primary"
                className="flex items-center bg-idecide-primary text-white hover:bg-idecide-primary-dark text-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Add Trusted Advisor</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-idecide-primary/20">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="overflow-x-auto scrollbar-hide">
            <nav className="flex space-x-2 sm:space-x-4 lg:space-x-8 min-w-max">
              {tabs.map((tab) => {
                const badge = getTabBadge(tab.id);
                const hasAlert = getTabAlert(tab.id);
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-2 sm:px-3 py-4 text-sm font-medium border-b-2 transition-colors relative whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-idecide-primary text-idecide-primary'
                        : 'border-transparent text-idecide-dark hover:text-idecide-primary hover:border-idecide-primary/40'
                    }`}
                    title={tab.description}
                  >
                    {tab.icon}
                    <span className="ml-1 sm:ml-2">{tab.label}</span>
                    {badge && (
                      <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium ${
                        hasAlert 
                          ? 'bg-idecide-warning text-idecide-dark' 
                          : 'bg-idecide-bg text-idecide-dark'
                      }`}>
                        {badge}
                      </span>
                    )}
                    {hasAlert && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-idecide-warning rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Manual Refresh Button */}
        <div className="mb-6 flex justify-end">
          <Button 
            onClick={loadDashboard} 
            variant="outline" 
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh Dashboard'}
          </Button>
        </div>

        {activeTab === 'dashboard' && (
          loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-gray-600">Loading trusted advisor dashboard...</p>
              </div>
            </div>
          ) : dashboard ? (
            <TrustedAdvisorDashboard 
              dashboard={dashboard} 
              onRefresh={loadDashboard}
            />
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <AlertTriangle className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Dashboard</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={loadDashboard} variant="primary">
                Try Again
              </Button>
            </div>
          ) : null
        )}
        
        {activeTab === 'advisors' && (
          <TrustedAdvisorList 
            onRefresh={loadDashboard} 
            onAddAdvisor={() => setShowInvitationModal(true)}
          />
        )}
        
        {activeTab === 'permissions' && (
          <PermissionMatrix 
            onRefresh={loadDashboard}
            onAddAdvisor={() => setShowInvitationModal(true)}
          />
        )}
        
        {activeTab === 'unlock-requests' && (
          <UnlockRequests onRefresh={loadDashboard} />
        )}
        
        {activeTab === 'emergency-contacts' && (
          <EmergencyContacts onRefresh={loadDashboard} />
        )}
        
        {activeTab === 'audit-logs' && (
          <AuditLogs />
        )}
      </div>

      {/* Invitation Modal */}
      <TrustedAdvisorInvitationModal
        isOpen={showInvitationModal}
        onClose={() => setShowInvitationModal(false)}
        onInvite={handleInvitationSuccess}
      />

      {/* Quick Actions Floating Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6">
        <div className="flex flex-col space-y-2">
          {dashboard?.pending_unlock_requests.length > 0 && (
            <Button
              onClick={() => setActiveTab('unlock-requests')}
              variant="warning"
              className="flex items-center shadow-lg text-sm"
            >
              <Unlock className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{dashboard.pending_unlock_requests.length} Pending Requests</span>
              <span className="sm:hidden">{dashboard.pending_unlock_requests.length} Requests</span>
            </Button>
          )}
          {dashboard?.summary.pending_advisors > 0 && (
            <Button
              onClick={() => setActiveTab('advisors')}
              variant="outline"
              className="flex items-center shadow-lg bg-white text-sm"
            >
              <Clock className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{dashboard.summary.pending_advisors} Pending Invitations</span>
              <span className="sm:hidden">{dashboard.summary.pending_advisors} Invitations</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrustedAdvisorManagement; 